import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { payments, auth } from '../api';

export default function PaymentCallback() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState('verifying'); // verifying | success | failed
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const reference = params.get('reference') || params.get('trxref');

  useEffect(() => {
    if (!reference) {
      setState('failed');
      setError('Missing payment reference');
      return;
    }
    (async () => {
      try {
        const res = await payments.verify(reference);
        if (res.status === 'success' && res.payment_status === 'paid') {
          setResult(res);
          setState('success');
          // Refresh stored user so sidebar + accessStatus update
          try { await auth.me(); } catch { /* ignore 403 edge-cases */ }
          // Auto-redirect after 2.5s
          setTimeout(() => nav('/dashboard'), 2500);
        } else {
          setState('failed');
          setError('Payment did not complete. You can try again or contact support.');
        }
      } catch (err) {
        setState('failed');
        setError(err?.response?.data?.detail || 'Could not verify payment');
      }
    })();
    // eslint-disable-next-line
  }, [reference]);

  return (
    <div className="min-h-screen bg-stone-950 grid place-items-center p-6">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl text-center" data-testid="payment-callback">
        {state === 'verifying' && (
          <>
            <Loader2 size={28} className="mx-auto animate-spin text-emerald-700"/>
            <h1 className="mt-4 font-serif text-xl font-semibold text-stone-900">Verifying your payment…</h1>
            <p className="mt-1 text-sm text-stone-500">This usually takes a few seconds. Please don't close the tab.</p>
          </>
        )}
        {state === 'success' && (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 text-emerald-800 grid place-items-center"><CheckCircle2 size={28}/></div>
            <h1 className="mt-4 font-serif text-2xl font-semibold text-stone-900">Payment successful</h1>
            <p className="mt-1 text-sm text-stone-600">Your Accu Desk subscription is now active.</p>
            {result && (
              <div className="mt-4 inline-flex items-center gap-2 text-xs text-stone-500">
                <span className="tabular-nums">R {Number(result.amount || 0).toLocaleString()}</span>
                <span>·</span>
                <span className="capitalize">{result.plan} plan</span>
              </div>
            )}
            <Button onClick={() => nav('/dashboard')} className="mt-6 w-full bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="callback-dashboard-btn">
              Go to dashboard <ArrowRight size={14} className="ml-2"/>
            </Button>
            <div className="mt-2 text-[11px] text-stone-400">Redirecting automatically…</div>
          </>
        )}
        {state === 'failed' && (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-red-100 text-red-700 grid place-items-center"><XCircle size={28}/></div>
            <h1 className="mt-4 font-serif text-2xl font-semibold text-stone-900">Payment didn't complete</h1>
            <p className="mt-1 text-sm text-stone-600">{error}</p>
            <div className="mt-6 flex gap-2">
              <Button variant="outline" onClick={() => nav('/pay')} className="flex-1">Try again</Button>
              <Button onClick={() => nav('/dashboard')} className="flex-1 bg-emerald-800 hover:bg-emerald-900 text-white">Back to dashboard</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
